import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listingAgentResearchApi,
  type ListingAgentResearchRunDto,
  type ListingAgentResearchWorkspaceDto,
} from "@/api/listingAgentResearch";
import { extractErrorMessage } from "@/api/client";
import { Button, Label, Textarea, Timestamp } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { safeHref } from "@/design-system/lib/safeHref";
import { listingAgentResearchKeys } from "@/hooks/buyers/listingAgentResearchKeys";
import { DetailField, DetailFieldList, DetailSection } from "../../detailBits";
import { agentResearchCopy } from "./agentResearchCopy";

/** A stored URL as a link only through safeHref (R17); otherwise as text. */
export function SafeLink({ url, testId }: { url: string | null | undefined; testId?: string }) {
  if (!url) return null;
  const href = safeHref(url);
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="break-all text-gold-shadow hover:underline" data-testid={testId}>
      {url}
    </a>
  ) : (
    <span className="break-all" data-testid={testId}>
      {url}
    </span>
  );
}

/**
 * ① Start (D9) — what the research starts from: the listing's own facts, the
 * agent already attached, the contacts the description names, and the last saved
 * research. START RESEARCH queues a run (Listings.Manage only); while one is
 * active the stage shows it instead of offering a second.
 */
export function StartStage({
  workspace,
  canManage,
  onStarted,
  onWatchActive,
}: {
  workspace: ListingAgentResearchWorkspaceDto;
  canManage: boolean;
  onStarted: (run: ListingAgentResearchRunDto) => void;
  onWatchActive: (runId: string) => void;
}) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState("");
  const f = workspace.facts;
  const lastSaved = workspace.runs.find((r) => r.savedAtUtc);

  const start = useMutation({
    mutationFn: () =>
      listingAgentResearchApi.start(workspace.listingId, { notes: notes.trim() || null }),
    onSuccess: (run) => {
      void qc.invalidateQueries({ queryKey: listingAgentResearchKeys.forListing(workspace.listingId) });
      onStarted(run);
    },
  });

  const cityState = [f.city, f.state].filter(Boolean).join(", ");
  const money = (v?: number | null) => (v == null ? null : formatCurrency(v));

  return (
    <div className="grid gap-space-5" data-testid="agent-research-start">
      <DetailSection title="What the research starts from">
        <DetailFieldList>
          <DetailField label="Listing" value={f.displayName} />
          <DetailField label="URL" wide value={<SafeLink url={f.rawUrl} testId="agent-research-start-url" />} />
          <DetailField label="Marketplace" value={f.marketplaceName} />
          <DetailField label="Listing reference" value={f.listingReference} />
          <DetailField label="Dedupe key" value={f.normalizedUrl} wide />
          <DetailField label="Clef" value={f.clefLinked ? "Linked ✓" : "Not linked ✗"} />
          {f.headline && f.headline !== f.displayName && <DetailField label="Headline" value={f.headline} wide />}
          <DetailField label="City / state" value={cityState || null} />
          <DetailField label="Industry" value={f.industry} />
          <DetailField label="Asking price" value={money(f.askingPrice)} />
          <DetailField label="Cash flow" value={money(f.cashFlow)} />
          <DetailField label="Revenue" value={money(f.revenue)} />
          <DetailField
            label="Agent on file"
            value={f.agent ? [f.agent.displayName, f.agent.isFirmContact ? null : f.agent.firmName].filter(Boolean).join(" · ") : null}
          />
          <DetailField label="Contact form" wide value={<SafeLink url={f.contactFormUrl} />} />
          <DetailField
            label="Emails in the description"
            wide
            value={f.descriptionEmails.length ? f.descriptionEmails.join(", ") : null}
          />
          <DetailField
            label="Phones in the description"
            wide
            value={f.descriptionPhones.length ? f.descriptionPhones.join(", ") : null}
          />
          <DetailField
            label="Names in the description"
            wide
            value={f.descriptionNames.length ? f.descriptionNames.join(", ") : null}
          />
          <DetailField
            label="Last saved research"
            value={lastSaved?.savedAtUtc ? <Timestamp value={lastSaved.savedAtUtc} /> : null}
          />
        </DetailFieldList>
      </DetailSection>

      {workspace.activeRunId ? (
        <div className="grid gap-space-2" role="status" data-testid="agent-research-already-running">
          <p className="text-small text-onyx m-0">{agentResearchCopy.alreadyRunning}</p>
          <div>
            <Button variant="secondary" size="sm" onClick={() => onWatchActive(workspace.activeRunId!)}>
              Watch it
            </Button>
          </div>
        </div>
      ) : !workspace.enabled ? (
        <p className="text-small text-gray-mid m-0" data-testid="agent-research-disabled">
          {agentResearchCopy.switchedOff}
        </p>
      ) : canManage ? (
        <div className="grid gap-space-2">
          <Label htmlFor="agent-research-start-notes">Anything the research should know (optional)</Label>
          <Textarea
            id="agent-research-start-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="agent-research-start-notes"
          />
          {start.isError && (
            <p className="text-small text-status-error m-0" role="alert">
              {extractErrorMessage(start.error)}
            </p>
          )}
          <p className="text-small text-gray-mid m-0" data-testid="agent-research-start-scope">
            {agentResearchCopy.startScope}
          </p>
          <div>
            <Button onClick={() => start.mutate()} disabled={start.isPending} data-testid="agent-research-start-button">
              {start.isPending ? "Starting…" : "Start research"}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
